import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";

/**
 * Section wrapper with optional subtitle, toolbar (right), footer and compact mode.
 * Now supports collapsing with smooth animations.
 *
 * Props:
 * - title: string
 * - subtitle?: string | ReactNode
 * - right?: ReactNode (actions)
 * - footer?: ReactNode
 * - compact?: boolean (reduces paddings)
 * - id?: string (for deep links & a11y)
 * - collapsible?: boolean (default: true)
 * - initialCollapsed?: boolean (default: false)
 * - onCollapseChange?: (collapsed: boolean) => void
 * - persistKey?: string (if provided or id exists, collapse state is saved to localStorage)
 * - keepMounted?: boolean (default: false) keep children mounted when collapsed
 * - toggleOnHeader?: boolean (default: false) click header area to toggle
 */
export default function SectionCard({
  title,
  subtitle,
  right,
  footer,
  compact = false,
  id,
  collapsible = true,
  initialCollapsed = false,
  onCollapseChange,
  persistKey,
  keepMounted = false,
  toggleOnHeader = false,
  children,
}) {
  const storageKey = useMemo(() => {
    if (!collapsible) return null;
    return persistKey || (id ? `sectioncard:${id}:collapsed` : null);
  }, [collapsible, persistKey, id]);

  const [collapsed, setCollapsed] = useState(() => {
    if (!storageKey || typeof window === "undefined") return initialCollapsed;
    try {
      const v = window.localStorage.getItem(storageKey);
      if (v === null) return initialCollapsed;
      return v === "1";
    } catch {
      return initialCollapsed;
    }
  });

  useEffect(() => {
    if (!storageKey || typeof window === "undefined") return;
    try {
      window.localStorage.setItem(storageKey, collapsed ? "1" : "0");
    } catch {
      /* ignore */
    }
  }, [collapsed, storageKey]);

  useEffect(() => {
    onCollapseChange?.(collapsed);
  }, [collapsed, onCollapseChange]);

  const contentId = id ? `${id}-content` : undefined;
  const EASE = [0.22, 1, 0.36, 1];

  const toggle = () => {
    if (!collapsible) return;
    setCollapsed((c) => !c);
  };

  const onHeaderKeyDown = (e) => {
    if (!toggleOnHeader || !collapsible) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggle();
    }
  };

  return (
    <motion.section
      id={id}
      layout
      data-collapsed={collapsed ? "true" : "false"}
      className={[
        "bg-white rounded-2xl border border-gray-100 shadow-sm",
        "transition-shadow hover:shadow-md focus-within:shadow-md",
        compact ? "p-4" : "p-4 sm:p-6",
      ].join(" ")}
      aria-labelledby={id ? `${id}-title` : undefined}
    >
      <motion.div
        layout
        className="flex items-start justify-between gap-4"
        {...(toggleOnHeader && collapsible
          ? {
              role: "button",
              tabIndex: 0,
              "aria-expanded": !collapsed,
              "aria-controls": contentId,
              onClick: toggle,
              onKeyDown: onHeaderKeyDown,
            }
          : {})}
      >
        <div className="min-w-0">
          <h3
            className="text-lg font-semibold text-gray-900 truncate"
            id={id ? `${id}-title` : undefined}
          >
            {title}
          </h3>
          {subtitle ? (
            <p className="mt-1 text-sm text-gray-500">{subtitle}</p>
          ) : null}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {right ? <div className="flex items-center gap-2">{right}</div> : null}

          {collapsible ? (
            <button
              type="button"
              onClick={toggle}
              className={[
                "inline-flex items-center justify-center rounded-xl",
                "border border-gray-200 bg-gray-50 hover:bg-gray-100",
                "px-2.5 py-1.5 text-sm font-medium text-gray-700",
                "shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500",
              ].join(" ")}
              aria-expanded={!collapsed}
              aria-controls={contentId}
              title={collapsed ? "Expand" : "Collapse"}
            >
              <span className="sr-only">{collapsed ? "Expand" : "Collapse"}</span>
              <motion.span
                aria-hidden
                className="inline-flex"
                animate={{ rotate: collapsed ? 0 : 180 }}
                transition={{ duration: 0.22, ease: EASE }}
              >
                <ChevronDown className="h-4 w-4" />
              </motion.span>
            </button>
          ) : null}
        </div>
      </motion.div>

      {/* subtle divider only when content is visible */}
      <AnimatePresence initial={false}>
        {!collapsed && (
          <motion.div
            key="divider-top"
            layout
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="mt-4 mb-5 h-px bg-gray-100"
          />
        )}
      </AnimatePresence>

      {/* Content area with height + opacity animation */}
      <AnimatePresence initial={false}>
        {(!collapsed || keepMounted) && (
          <motion.div
            key="content"
            layout
            initial={collapsed ? "collapsed" : "open"}
            animate={collapsed ? "collapsed" : "open"}
            exit="collapsed"
            variants={{
              open: {
                height: "auto",
                opacity: 1,
                transition: {
                  height: { duration: 0.36, ease: EASE },
                  opacity: { duration: 0.2, ease: EASE, delay: 0.06 },
                },
              },
              collapsed: {
                height: 0,
                opacity: 0,
                transition: {
                  height: { duration: 0.28, ease: EASE },
                  opacity: { duration: 0.18, ease: EASE },
                },
              },
            }}
            style={{ overflow: "hidden" }}
            id={contentId}
            aria-hidden={collapsed}
          >
            <div>{children}</div>

            {footer ? (
              <>
                <div className="mt-5 mb-4 h-px bg-gray-100" />
                <div className="text-sm text-gray-600">{footer}</div>
              </>
            ) : null}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.section>
  );
}
